import React, { useState, useRef } from 'react';
import {
  Box, Typography, TextField, Button, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, LinearProgress,
  InputAdornment, Chip, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useSelector } from 'react-redux';

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function ModelBrowser({ models, loading, uploading, selectedModelId, onSelect, onUpload, onDelete, onClose }) {
  const theme = useSelector((state) => state.themedata.theme);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const fileInputRef = useRef(null);

  const isDark = theme === 'dark';
  const isBusy = loading || uploading || deleting;

  const filtered = models.filter((m) =>
    (m.displayName || m.filename).toLowerCase().includes(search.toLowerCase())
  );

  const confirmDeleteModel = models.find((m) => m.id === confirmDeleteId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await onUpload(file, file.name.replace(/\.[^/.]+$/, ''));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await onDelete(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (err) {
      setDeleteError(
        err.response?.data?.error || err.response?.data?.detail || err.message || 'Delete failed'
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleDialogClose = () => {
    if (deleting) return; // block close while in progress
    setConfirmDeleteId(null);
    setDeleteError(null);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: 340,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDark ? '#1a1a1a' : '#fafafa',
        borderRight: `1px solid ${isDark ? '#333' : '#ddd'}`,
        zIndex: 3,
        pointerEvents: isBusy && !deleting ? 'none' : 'all',
        opacity: isBusy && !deleting ? 0.6 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Top section */}
      <Box sx={{ padding: 2, borderBottom: `1px solid ${isDark ? '#333' : '#ddd'}` }}>
        <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 1.5 }}>
          3D Models
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search models..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDark ? '#888' : '#666' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? '#252525' : '#fff',
              color: isDark ? '#fff' : '#000',
            },
            mb: 1,
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".glb,.gltf"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
        <Button
          fullWidth
          variant="outlined"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          disabled={uploading || loading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Uploading…' : 'Upload Model'}
        </Button>

        {/* Upload progress bar */}
        {uploading && (
          <LinearProgress
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #008CD7, #00C9FF)',
              },
            }}
          />
        )}
      </Box>

      {/* Model list */}
      <List sx={{ flex: 1, overflow: 'auto', padding: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ color: isDark ? '#888' : '#666' }}>
              Loading models…
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Typography sx={{ padding: 2, color: isDark ? '#888' : '#666', textAlign: 'center' }}>
            {models.length === 0 ? 'No models uploaded yet.' : 'No models match your search.'}
          </Typography>
        ) : (
          filtered.map((model) => (
            <ListItem
              key={model.id}
              button
              selected={model.id === selectedModelId}
              onClick={() => onSelect(model.id)}
              sx={{
                borderBottom: `1px solid ${isDark ? '#222' : '#eee'}`,
                '&.Mui-selected': { backgroundColor: isDark ? '#0d47a1' : '#e3f2fd' },
              }}
            >
              <InsertDriveFileIcon sx={{ color: '#008CD7', mr: 1.5, fontSize: 28 }} />
              <ListItemText
                primary={model.displayName || model.filename}
                secondary={
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                    <Chip label={formatBytes(model.fileSize)} size="small" variant="outlined" />
                    {model.uploadedAt && (
                      <Typography variant="caption" sx={{ color: isDark ? '#888' : '#666' }}>
                        {new Date(model.uploadedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                }
                primaryTypographyProps={{ sx: { color: isDark ? '#fff' : '#000', fontWeight: 500 } }}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(model.id); setDeleteError(null); }}
                >
                  <DeleteIcon fontSize="small" sx={{ color: isDark ? '#888' : '#666' }} />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        )}
      </List>

      {onClose && (
        <Box sx={{ padding: 1, borderTop: `1px solid ${isDark ? '#333' : '#ddd'}` }}>
          <Button fullWidth onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </Box>
      )}

      {/* Delete confirm dialog */}
      <Dialog open={!!confirmDeleteId} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Delete Model?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{deleteError}</Alert>
          )}
          <Typography variant="body2">
            Permanently delete{' '}
            <strong>{confirmDeleteModel?.displayName || confirmDeleteModel?.filename}</strong>?
            <br />
            All associated component bindings will also be removed.
          </Typography>
          {deleting && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Deleting…
              </Typography>
              <LinearProgress
                sx={{
                  height: 5,
                  borderRadius: 3,
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #f44336, #ff7961)',
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDialogClose} disabled={deleting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default React.memo(ModelBrowser);
